import React, { useCallback, useEffect, useState } from 'react';
import { MailCheck, RefreshCw, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getApiErrorMessage } from '../services/api';
import './EmailVerificationGatePage.css';

interface EmailVerificationGatePageProps {
  mockEmail?: string;
  disableActions?: boolean;
}

const EmailVerificationGatePage: React.FC<EmailVerificationGatePageProps> = ({
  mockEmail,
  disableActions = false,
}) => {
  const { currentUser, logout, sendVerificationEmail, refreshVerificationStatus } = useAuth();
  const [sending, setSending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const effectiveEmail = mockEmail || currentUser?.email || '';
  const actionsDisabled = disableActions || !currentUser;

  useEffect(() => {
    if (disableActions || !currentUser?.uid) return;
    let cancelled = false;
    setSending(true);
    void sendVerificationEmail('verify-email-initial:v1').then(() => {
      if (!cancelled) setMessage('Verification email requested. Check your inbox (and spam).');
    }).catch((err) => {
      if (!cancelled) setError(getApiErrorMessage(err, 'Unable to request your verification email. Please retry.'));
    }).finally(() => {
      if (!cancelled) setSending(false);
    });
    return () => { cancelled = true; };
  }, [currentUser?.uid, disableActions, sendVerificationEmail]);

  const handleResend = async () => {
    setError(null);
    setMessage(null);
    if (!currentUser) {
      setError('Please sign in to resend a verification email.');
      return;
    }
    try {
      setSending(true);
      await sendVerificationEmail();
      setMessage('Verification email requested. Check your inbox (and spam).');
    } catch (err: any) {
      console.error('Failed to resend verification email:', err);
      setError(getApiErrorMessage(err, 'Failed to send verification email. Please try again.'));
    } finally {
      setSending(false);
    }
  };

  const handleRefresh = useCallback(async () => {
    setError(null);
    setMessage(null);
    if (!currentUser) {
      setError('Please sign in to refresh verification status.');
      return;
    }

    try {
      setRefreshing(true);
      const verified = await refreshVerificationStatus();
      setMessage(verified ? 'Email verified.' : 'Your email is not verified yet. Open the link in your verification email.');
    } catch (err) {
      console.error('Failed to refresh verification status:', err);
      setError('Failed to refresh status. Please try again.');
    } finally {
      setRefreshing(false);
    }
  }, [currentUser, refreshVerificationStatus]);

  useEffect(() => {
    if (actionsDisabled) return;
    const refreshOnReturn = () => {
      if (document.visibilityState === 'visible') void handleRefresh();
    };
    window.addEventListener('focus', refreshOnReturn);
    document.addEventListener('visibilitychange', refreshOnReturn);
    return () => {
      window.removeEventListener('focus', refreshOnReturn);
      document.removeEventListener('visibilitychange', refreshOnReturn);
    };
  }, [actionsDisabled, handleRefresh]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error('Failed to log out:', err);
    }
  };

  return (
    <div className="verify-page">
      <div className="verify-container">
        <div className="verify-header">
          <MailCheck size={28} />
          <h1>Verify your email</h1>
          <p>
            Verify <strong>{effectiveEmail}</strong> using the link in your email to continue.
          </p>
        </div>

        {error && <div className="verify-error">{error}</div>}
        {message && <div className="verify-message">{message}</div>}

        <div className="verify-actions">
          <button className="verify-primary" onClick={handleResend} disabled={actionsDisabled || sending || refreshing}>
            {sending ? 'Sending...' : 'Resend verification email'}
          </button>
          <button className="verify-secondary" onClick={handleRefresh} disabled={actionsDisabled || sending || refreshing}>
            <RefreshCw size={16} />
            {refreshing ? 'Refreshing...' : "I've verified, refresh"}
          </button>
          <button className="verify-logout" onClick={handleLogout} disabled={disableActions || sending || refreshing}>
            <LogOut size={16} />
            Logout
          </button>
        </div>

        <div className="verify-help">
          <p>If you don’t see the email within a few minutes, check spam or try resending.</p>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationGatePage;
